/** The instruments the desk quotes, and the deterministic series drawn against them (CMP-MKT).
 *
 *  One table and one generator, in contracts, because three places need to agree and any two of them drifting is a
 *  price that is wrong on one screen and right on another: the desk's mini-chart draws this series, the seed writes
 *  it into `candles`, and the chart datafeed serves it back. Before this the table lived in a web module and the
 *  `candles` table was empty, so the TradingView chart drew a *fourth* series — the design package's own client-side
 *  synthetic walk — with nothing tying it to the prices the ticket was sized against.
 *
 *  The numbers are the design reference's own. `XAUUSD` is the instrument the whole reference was drawn against, so
 *  its series must stay identical to the digit or every figure on the desk moves; that is why its symbol seed is
 *  zero and why volatility is expressed as a ratio against gold's base rather than as an absolute.
 */

export interface InstrumentSpec {
  /** The price the series walks from. */
  readonly base: number;
  /** How the instrument is quoted. A price shown to the wrong precision is a different price. */
  readonly decimals: number;
  /** Half the spread, in price units — the distance from mid to bid and to ask. */
  readonly halfSpread: number;
  /** The spread as the desk displays it. */
  readonly spread: string;
}

export const INSTRUMENTS: Record<string, InstrumentSpec> = {
  XAUUSD: { base: 4036, decimals: 2, halfSpread: 0.1, spread: '20' },
  EURUSD: { base: 1.13761, decimals: 5, halfSpread: 0.00003, spread: '0.6' },
  GBPJPY: { base: 198.42, decimals: 3, halfSpread: 0.008, spread: '1.6' },
  US30: { base: 44120.5, decimals: 1, halfSpread: 2.1, spread: '4.2' },
  NAS100: { base: 20343.3, decimals: 1, halfSpread: 1.6, spread: '3.2' },
  USDJPY: { base: 163.787, decimals: 3, halfSpread: 0.0035, spread: '0.7' },
  BTCUSD: { base: 96226.2, decimals: 1, halfSpread: 12.25, spread: '24.5' },
};

export const DEFAULT_SYMBOL = 'XAUUSD';

/** The chart resolutions the datafeed serves, and the minutes each one spans. `1W` is served but not offered by the
 *  desk's own timeframe control. */
export const RESOLUTION_MINUTES: Record<string, number> = {
  '1': 1,
  '5': 5,
  '15': 15,
  '30': 30,
  '60': 60,
  '240': 240,
  '1D': 1440,
  '1W': 10_080,
};

export function instrumentSpec(symbol: string): InstrumentSpec {
  return INSTRUMENTS[symbol] ?? INSTRUMENTS[DEFAULT_SYMBOL]!;
}

export interface OhlcBar {
  /** Bar open, in epoch milliseconds. */
  readonly openAt: number;
  readonly open: number;
  readonly high: number;
  readonly low: number;
  readonly close: number;
  readonly volume: number;
}

/** The seeded walk, lifted from the reference's own `chart()` so the desk and the stored candles cannot disagree.
 *
 *  Deterministic in every input: the same symbol, step and bar count produce the same prices on any machine and in
 *  any order. The LCG constants and the `7 + stepMin + symSeed` seeding are the reference's; changing them changes
 *  every chart in the product and the seed checksum with it.
 *
 *  `endAt` is the open of the **last** bar, so a caller anchors the series where its data should end rather than
 *  discovering where the generator happened to stop.
 *
 *  The walk is then scaled so its **last close is the instrument's quoted price**. Without it the series drifts
 *  wherever the seed takes it — GBPJPY quoted at 197.839 on the desk header while its chart closed at 202.496 —
 *  and a trader reading a price above the ticket they are about to send is reading two different markets. Scaled
 *  multiplicatively, so every relative move and the shape of the series are unchanged; only the level moves. */
export function candleSeries(symbol: string, stepMinutes: number, count: number, endAt: number): OhlcBar[] {
  const spec = instrumentSpec(symbol);
  const symSeed =
    symbol === DEFAULT_SYMBOL ? 0 : [...symbol].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) % 997, 0);
  let seed = 7 + stepMinutes + symSeed;
  let price = spec.base;
  const rnd = (): number => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  // Proportional to the instrument, not absolute: 1.6 points is a gold bar and a EURUSD apocalypse.
  const vola = Math.sqrt(stepMinutes) * 1.6 * (spec.base / INSTRUMENTS[DEFAULT_SYMBOL]!.base);
  const stepMs = stepMinutes * 60_000;
  const round = (v: number): number => Number(v.toFixed(spec.decimals));

  const raw: { openAt: number; open: number; high: number; low: number; close: number; volume: number }[] =
    [];
  for (let i = 0; i < count; i++) {
    const open = price;
    const close = price + (rnd() - 0.47) * vola;
    const high = Math.max(open, close) + rnd() * vola * 0.45;
    const low = Math.min(open, close) - rnd() * vola * 0.45;
    // Volume in whole units: a chart's volume pane is a shape, and a fractional lot is not a thing a feed reports.
    const volume = Math.round(400 + (Math.abs(close - open) / vola) * 1200 + rnd() * 1400);
    raw.push({ openAt: endAt - (count - 1 - i) * stepMs, open, high, low, close, volume });
    price = close;
  }

  const last = raw[raw.length - 1];
  const scale = last && last.close !== 0 ? spec.base / last.close : 1;
  return raw.map((bar) => ({
    openAt: bar.openAt,
    open: round(bar.open * scale),
    high: round(bar.high * scale),
    low: round(bar.low * scale),
    close: round(bar.close * scale),
    volume: bar.volume,
  }));
}
